<template>
  <div class="w-full h-full">
    <!-- Mode aperçu (miniature) -->
    <div v-if="preview"
      class="flex items-center justify-center h-full text-gray-500">
      <div class="text-center">
        <div class="text-4xl mb-2">{{ getChartIcon() }}</div>
        <p class="text-sm font-medium">{{ chart.name }}</p>
        <p class="text-xs text-gray-400 mt-1">{{ formatPeriod() }}</p>
      </div>
    </div>

    <!-- Mode complet avec ChartRenderer ECharts -->
    <div v-else class="h-full">
      <ChartRenderer v-if="!isLoadingData" :config="chart.config"
        :series-data="seriesData"
        :is-mobile="isMobile"
        :compact-mode="compactMode" :show-header="showHeader"
        :show-stats="showStats" :show-debug-info="showDebugInfo"
        :height="height" :center-id="centerId" :report-id="reportId"
        @data-point-click="handleDataPointClick" @chart-ready="handleChartReady"
        @error="handleChartError" />
      <div v-else class="chart-renderer">
        <Loading />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { FLEXIBLE_CHART_TYPE_OPTIONS, type CustomChart } from '../../types/customCharts'
import ChartRenderer from '../../components/charts/ChartRenderer.vue'
import { SalesApi } from '../../api/SalesApi'
import type { SalesByIntervalResponse, ProductsReportResponse } from '../../responses'
import { DateTime } from 'luxon'
import Loading from '@/components/Loading.vue'

interface Props {
  chart: CustomChart
  preview?: boolean
  // Données pour les appels API
  centerId: number
  reportId?: string
  // Props existantes
  isMobile?: boolean
  compactMode?: boolean
  showHeader?: boolean
  showStats?: boolean
  showDebugInfo?: boolean
  height?: number
}

const props = withDefaults(defineProps<Props>(), {
  preview: false,
  isMobile: true,
  compactMode: false,
  showHeader: true,
  showStats: false,
  showDebugInfo: false,
  height: 400
})

interface Emits {
  (e: 'dataPointClick', data: any): void
  (e: 'chartReady', chart: any): void
  (e: 'chartError', error: string): void
}

const emit = defineEmits<Emits>()

// Données API réactives - une par série
const seriesData = ref<Map<string, { 
  main: SalesByIntervalResponse | ProductsReportResponse | undefined,
  comparison?: SalesByIntervalResponse | ProductsReportResponse | undefined 
}>>(new Map())
const isLoadingData = ref(false)

// Fonction pour récupérer les données API par série
const fetchChartData = async () => {
  if (props.preview || !props.centerId) return

  isLoadingData.value = true
  seriesData.value.clear()
  
  try {
    const config = props.chart.config
    const startDate = DateTime.fromISO(config.period.start)
    const realStartDate = startDate.set({
      hour: 4,
      minute: 0,
      second: 0
    })

    const endDate = DateTime.fromISO(config.period.end)
    const realEndDate = endDate.plus({
      days: 1
    }).set({
      hour: 3,
      minute: 59,
      second: 59
    })

    const interval = config.xAxis.timeAggregation

    if (!interval) {
      throw new Error('Invalid time aggregation')
    }

    // Préparer les dates de comparaison si nécessaire
    let comparisonStartDate: DateTime | undefined
    let realComparisonStartDate: DateTime | undefined
    let comparisonEndDate: DateTime | undefined
    let realComparisonEndDate: DateTime | undefined

    if (config.showComparison && config.comparisonPeriod) {
      comparisonStartDate = DateTime.fromISO(config.comparisonPeriod.start)
      realComparisonStartDate = comparisonStartDate.set({
        hour: 4,
        minute: 0,
        second: 0
      })

      comparisonEndDate = DateTime.fromISO(config.comparisonPeriod.end)
      realComparisonEndDate = comparisonEndDate.plus({
        days: 1
      }).set({
        hour: 3,
        minute: 59,
        second: 59
      })
    }

    // Récupérer les données pour chaque série individuellement
    const dataPromises = config.dataSources.map(async (dataSource) => {
      const origin = dataSource.origin || 'all'
      const deposit = dataSource.deposit || false
      const aggregation = dataSource.aggregation

      let mainData: SalesByIntervalResponse | ProductsReportResponse | undefined
      let comparisonData: SalesByIntervalResponse | ProductsReportResponse | undefined

      if (dataSource.source === 'sales') {
        // Récupérer les données de ventes pour cette série
        mainData = await SalesApi.fetchSalesByInterval(
          realStartDate, realEndDate, interval, origin, deposit, props.centerId,
          realComparisonStartDate, realComparisonEndDate, aggregation
        )

        if (config.showComparison && realComparisonStartDate && realComparisonEndDate) {
          comparisonData = await SalesApi.fetchSalesByInterval(
            realComparisonStartDate, realComparisonEndDate, interval, origin, deposit, props.centerId,
            undefined, undefined, aggregation
          )
        }
      } else if (dataSource.source === 'products') {
        // Récupérer les données de produits pour cette série
        mainData = await SalesApi.fetchProductsReport(
          realStartDate, realEndDate, interval, origin, deposit, props.centerId,
          comparisonStartDate, comparisonEndDate, aggregation
        )

        if (config.showComparison && comparisonStartDate && comparisonEndDate) {
          comparisonData = await SalesApi.fetchProductsReport(
            comparisonStartDate, comparisonEndDate, interval, origin, deposit, props.centerId,
            undefined, undefined, aggregation
          )
        }
      }

      // Stocker les données pour cette série
      seriesData.value.set(dataSource.id, {
        main: mainData,
        comparison: comparisonData
      })
    })

    // Attendre que tous les appels API se terminent
    await Promise.all(dataPromises)

  } catch (error) {
    console.error('Error fetching chart data:', error)
  } finally {
    isLoadingData.value = false
  }
}

const getChartIcon = (): string => {
  const option = FLEXIBLE_CHART_TYPE_OPTIONS.find(opt => opt.type === props.chart.config.chartType)
  return option?.icon || '📊'
}

const formatPeriod = (): string => {
  const start = new Date(props.chart.config.period.start).toLocaleDateString('fr-FR')
  const end = new Date(props.chart.config.period.end).toLocaleDateString('fr-FR')
  return `${start} - ${end}`
}

// Gestion des événements du ChartRenderer
const handleDataPointClick = (data: any) => {
  emit('dataPointClick', data)
}

const handleChartReady = (chart: any) => {
  emit('chartReady', chart)
}

const handleChartError = (error: string) => {
  emit('chartError', error)
}

// Hooks pour charger les données
onMounted(() => {
  fetchChartData()
})

// Recharger les données si la configuration change
watch(() => props.chart.config, () => {
  fetchChartData()
}, { deep: true })

// Recharger les données si centerId change
watch(() => props.centerId, () => {
  fetchChartData()
})
</script>